import React from "react";

//#region Types
import { AnnualReportTimelineProps } from "models/propTypes/annualAccountPropTypes";
import { ChartEnums } from "models/enums";
import { TimelineSubItem } from "models/propTypes/chartProps";
import { WintSuperkollAPIModelAnnualAccounts } from "wint-api-client/dist/types";
//#endregion
//#region Components
import SimpleTimeline from "components/common/charts/timeline/simpleTimeline";
//#endregion
import { TranslationKeys } from "translations";
import { LocalizationHelperInstance } from "utils/helpers";

const getStepState = (state: WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState, totalSteps: number) => {
  const orderedStates = [
    WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.AnnualReportSigningStarted,
    WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.GeneralMeetingSigningStarted,
    WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.XbrlFileSubmitted,
  ];

  const currentIndex = orderedStates.indexOf(state);

  // Generate the stepState dynamically
  return Array.from({ length: totalSteps }, (_, index) => {
    if (index < currentIndex) return ChartEnums.TimelineStepType.Done;
    if (index === currentIndex) return ChartEnums.TimelineStepType.InProgress;
    return ChartEnums.TimelineStepType.NotStarted;
  });
};

type AnnualReportSigner = WintSuperkollAPIModelAnnualAccounts.AnnualReportSigner;

const getSignerStatusText = (signer: AnnualReportSigner) => {
  if (!signer.hasSigned) {
    return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.AnnualReportSignerWaiting);
  }

  return signer.signDate
    ? LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.AnnualAccount.AnnualReportSignerSignedOn,
        undefined,
        { Date: LocalizationHelperInstance.getFormattedDate(signer.signDate) }
      )
    : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.AnnualReportSignerSigned);
};

const getSignerSubItem = (signer: AnnualReportSigner): TimelineSubItem => ({
  text: signer.name ?? "",
  subText: getSignerStatusText(signer),
  type: signer.hasSigned ? ChartEnums.TimelineStepType.Done : ChartEnums.TimelineStepType.InProgress,
});

const getSignedSummary = (signers: AnnualReportSigner[]) =>
  LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.AnnualAccount.AnnualReportSignersSignedCount,
    undefined,
    {
      Signed: signers.filter((signer) => signer.hasSigned).length,
      Total: signers.length,
    }
  );

const AnnualReportTimeline: React.FC<AnnualReportTimelineProps> = ({ annualReportState, signers }) => {
  const timelineSteps = [
    TranslationKeys.AnnualAccount.AnnualReportTimeLineStep1,
    TranslationKeys.AnnualAccount.AnnualReportTimeLineStep2,
    TranslationKeys.AnnualAccount.AnnualReportTimeLineStep3,
  ];

  const stepState = getStepState(annualReportState, timelineSteps.length);

  const showSigners = stepState[0] === ChartEnums.TimelineStepType.InProgress && (signers?.length ?? 0) > 1;

  const timelineItems = timelineSteps.map((stepKey, index) => ({
    text: LocalizationHelperInstance.getFormattedMessage(stepKey),
    type: stepState[index],
    subText: index === 0 && showSigners ? getSignedSummary(signers!) : undefined,
    subItems: index === 0 && showSigners ? signers!.map(getSignerSubItem) : undefined,
  }));

  return <SimpleTimeline items={timelineItems} />;
};

export default AnnualReportTimeline;
